import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import AllDeviceRow from "./AllDeviceRow";
import SearchBar from "./SearchBar";
import DeviceExportDropdown from "./DeviceExportDropdown";
import { FaSync, FaEdit, FaPowerOff, FaDownload, FaCloudDownloadAlt, FaArrowCircleUp, FaUsers } from "react-icons/fa";
import isAdministrator, { isStaff } from "../utilities/checkPrivileges";
import AssignUserModal from "./modals/AssignUserModal";
import {
  updateDevicesInventory,
  restartDevice,
  renameDevice,
  installProfile,
  getAvailableSoftwareUpdates,
  installAvailableOSUpdate
} from "../commands/mdmCommands";
import nanoCmdService from "../services/nanoCmdService";
import softwareUpdateService from "../services/softwareUpdateService";
import deviceGroupService from "../services/deviceGroupService";
import { toast } from "react-toastify";
import { GET_CONFIG_PROFILES } from "../queries/configProfilesQueries";
import { deviceLastSeenMs } from "../utilities/deviceLastSeen.js";

const DEVICE_TYPES = [
  { value: 'mac', label: 'Mac' },
  { value: 'iphone', label: 'iPhone' },
  { value: 'ipad', label: 'iPad' },
  { value: 'appletv', label: 'Apple TV' },
];

const CHECKIN_RANGES = [
  { value: '', label: 'Alle Zeiträume' },
  { value: 'today', label: 'Heute' },
  { value: 'week', label: 'Diese Woche' },
  { value: 'month', label: 'Dieser Monat' },
  { value: 'older', label: 'Älter' },
];

const TABLE_UI_PREFS_KEY = "allDevices.tableUiPrefs.v1";

const loadUiPrefs = () => {
  try {
    const raw = localStorage.getItem(TABLE_UI_PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
};

function getCheckinBounds(range) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const ranges = {
    today: { min: todayStart.getTime() / 1000 },
    week: { min: weekStart.getTime() / 1000 },
    month: { min: monthStart.getTime() / 1000 },
    older: { max: monthStart.getTime() / 1000 },
  };
  return ranges[range] || null;
}

const normalizeMac = (value) => String(value || "").toLowerCase().replace(/[^a-f0-9]/g, "");

export default function AllDeviceTable({ 
  deviceData, 
  totalCount, 
  hasNextPage, 
  onLoadMore,
  loadingMore,
  locations = [],
  groups = [],
  locationFilter = "",
  locationFilters = [],
  onLocationFilterChange,
  onLocationFiltersChange,
  deviceTypeFilters = [],
  onDeviceTypeFiltersChange,
  checkinRange = "",
  onCheckinRangeChange,
  osVersionFilter = "",
  onOsVersionFilterChange,
  groupFilters = [],
  onGroupFiltersChange,
  registrations = [],
  onDevicesUpdated,
  refetch
}) {
  const initialUiPrefs = loadUiPrefs();
  const [searchQuery, setSearchQuery] = useState(initialUiPrefs?.searchQuery || "");
  const [sortConfig, setSortConfig] = useState(
    initialUiPrefs?.sortConfig?.key && initialUiPrefs?.sortConfig?.direction
      ? initialUiPrefs.sortConfig
      : { key: 'DeviceName', direction: 'asc' }
  );
  const [selectedSerials, setSelectedSerials] = useState(new Set());
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTemplate, setRenameTemplate] = useState("");
  /** Wenn true: nur Platzhalter-Vorlage, Registrierungs-Namen ignorieren */
  const [renameIgnorePlanned, setRenameIgnorePlanned] = useState(false);
  const [showInstallProfileModal, setShowInstallProfileModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [showNanoCmdModal, setShowNanoCmdModal] = useState(false);
  const [nanocmdAvailable, setNanocmdAvailable] = useState(false);
  const [nanocmdCmdPlans, setNanocmdCmdPlans] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [suPolicies, setSuPolicies] = useState({});
  const [suBusy, setSuBusy] = useState("");
  const [assignTarget, setAssignTarget] = useState(null);
  const postActionRefreshTimeoutRef = useRef([]);

  const isAdmin = isAdministrator();
  const staff = isStaff();
  const { data: profilesData } = useQuery(GET_CONFIG_PROFILES);
  const configProfiles = profilesData?.configProfiles || [];

  useEffect(() => {
    nanoCmdService.getStatus().then((r) => {
      if (r?.available) {
        setNanocmdAvailable(true);
        nanoCmdService.getCmdPlans().then((plans) => setNanocmdCmdPlans(plans || []));
      } else {
        setNanocmdAvailable(false);
      }
    }).catch(() => setNanocmdAvailable(false));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        TABLE_UI_PREFS_KEY,
        JSON.stringify({
          searchQuery,
          sortConfig
        })
      );
    } catch (e) {
      // ignore storage write errors
    }
  }, [searchQuery, sortConfig]);

  useEffect(() => () => {
    postActionRefreshTimeoutRef.current.forEach((id) => clearTimeout(id));
    postActionRefreshTimeoutRef.current = [];
  }, []);

  const schedulePostActionRefetch = (label = "Aktion") => {
    if (!refetch) return;

    postActionRefreshTimeoutRef.current.forEach((id) => clearTimeout(id));
    postActionRefreshTimeoutRef.current = [];

    const delays = [3000, 8000, 15000, 30000, 45000, 60000];
    delays.forEach((delay) => {
      const id = setTimeout(() => {
        refetch().catch(() => {});
      }, delay);
      postActionRefreshTimeoutRef.current.push(id);
    });

    toast.info(`${label}: automatische Aktualisierung läuft im Hintergrund.`);
  };

  const requestSort = (key) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        key,
        direction: 'asc'
      };
    });
  };

  const serialToRegistration = useMemo(() => {
    const map = {};
    (registrations || []).forEach(r => {
      map[r.serialNumber] = r;
    });
    return map;
  }, [registrations]);

  const serialsInSelectedGroups = useMemo(() => {
    if (!groupFilters?.length) return null;
    const set = new Set();
    (registrations || []).forEach(r => {
      const regGroups = (r.groups || (r.group ? [r.group] : [])).map(g => (typeof g === 'object' ? g?._id : g)).filter(Boolean);
      if (groupFilters.some(gid => regGroups.includes(gid))) {
        set.add(r.serialNumber);
      }
    });
    return set;
  }, [registrations, groupFilters]);

  const sortedAndFilteredDevices = useMemo(() => {
    if (!deviceData) return [];
    
    let sorted = [...deviceData];

    if (deviceTypeFilters?.length) {
      sorted = sorted.filter(d => deviceTypeFilters.includes(d.type?.toLowerCase()));
    }

    const locIds = (locationFilters?.length ? locationFilters : (locationFilter ? [locationFilter] : [])).filter(Boolean);
    if (locIds.length) {
      sorted = sorted.filter(d => d.location?._id && locIds.includes(d.location._id));
    }

    const checkinBounds = getCheckinBounds(checkinRange);
    if (checkinBounds) {
      sorted = sorted.filter(d => {
        const ts = Math.floor(deviceLastSeenMs(d) / 1000);
        if (checkinBounds.min !== undefined && ts < checkinBounds.min) return false;
        if (checkinBounds.max !== undefined && ts >= checkinBounds.max) return false;
        return true;
      });
    }

    if (osVersionFilter?.trim()) {
      const q = osVersionFilter.toLowerCase().trim();
      sorted = sorted.filter(d => {
        const v = (d.QueryResponses?.OSVersion || d.OSVersion || '').toLowerCase();
        return v.includes(q);
      });
    }

    if (serialsInSelectedGroups) {
      sorted = sorted.filter(d => serialsInSelectedGroups.has(d.SerialNumber));
    }
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'SerialNumber':
          aValue = a.SerialNumber;
          bValue = b.SerialNumber;
          break;
        case 'DeviceName':
          aValue = a.QueryResponses?.DeviceName || '';
          bValue = b.QueryResponses?.DeviceName || '';
          break;
        case 'Model':
          aValue = a.ProductName || '';
          bValue = b.ProductName || '';
          break;
        case 'Location':
          aValue = a.location?.schoolNumber || a.location?.name || '';
          bValue = b.location?.schoolNumber || b.location?.name || '';
          break;
        case 'AssetTag':
          aValue = a.assetTag || '';
          bValue = b.assetTag || '';
          break;
        case 'OSVersion':
          aValue = a.OSVersion || '0';
          bValue = b.OSVersion || '0';
          break;
        case 'updatedAt':
          aValue = deviceLastSeenMs(a);
          bValue = deviceLastSeenMs(b);
          // Return the comparison directly for timestamps
          return sortConfig.direction === 'asc' 
            ? aValue - bValue
            : bValue - aValue;
        default:
          aValue = a.QueryResponses?.DeviceName || '';
          bValue = b.QueryResponses?.DeviceName || '';
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    const lowerQuery = searchQuery.toLowerCase().trim();
    if (lowerQuery) {
      const normalizedQuery = normalizeMac(lowerQuery);
      sorted = sorted.filter((device) => (
        device.QueryResponses?.DeviceName?.toLowerCase().includes(lowerQuery) ||
        device.SerialNumber?.toLowerCase().includes(lowerQuery) ||
        device.assetTag?.toLowerCase().includes(lowerQuery) ||
        device.location?.schoolNumber?.toLowerCase().includes(lowerQuery) ||
        device.location?.name?.toLowerCase().includes(lowerQuery) ||
        (normalizedQuery.length >= 6 && (
          normalizeMac(device.QueryResponses?.WiFiMAC).includes(normalizedQuery) ||
          normalizeMac(device.QueryResponses?.EthernetMAC).includes(normalizedQuery) ||
          normalizeMac(device.QueryResponses?.BluetoothMAC).includes(normalizedQuery) ||
          normalizeMac(device.QueryResponses?.DeviceID).includes(normalizedQuery)
        ))
      ));
    }

    return sorted;
  }, [deviceData, sortConfig, searchQuery, deviceTypeFilters, locationFilter, locationFilters, checkinRange, osVersionFilter, serialsInSelectedGroups]);

  const visibleUdidsKey = useMemo(
    () => (sortedAndFilteredDevices || []).map((device) => device.UDID).filter(Boolean).join(","),
    [sortedAndFilteredDevices]
  );

  useEffect(() => {
    const udids = visibleUdidsKey ? visibleUdidsKey.split(",") : [];
    if (!udids.length) {
      setSuPolicies({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      softwareUpdateService.lookupPolicies(udids)
        .then((data) => {
          if (!cancelled) setSuPolicies(data.policies || {});
        })
        .catch(() => {
          if (!cancelled) setSuPolicies({});
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [visibleUdidsKey]);

  const deviceCounts = useMemo(() => ({
    mac: deviceData?.filter(d => d.type === "mac").length || 0,
    iphone: deviceData?.filter(d => d.type === "iphone").length || 0,
    ipad: deviceData?.filter(d => d.type === "ipad").length || 0,
    appletv: deviceData?.filter(d => d.type === "appletv").length || 0
  }), [deviceData]);

  const exportSerialNumbersByType = useMemo(() => {
    const map = { mac: [], iphone: [], ipad: [], appletv: [] };
    (sortedAndFilteredDevices || []).forEach((d) => {
      const t = String(d.type || '').toLowerCase();
      if (map[t] && d.SerialNumber) map[t].push(d.SerialNumber);
    });
    return map;
  }, [sortedAndFilteredDevices]);

  const allFilteredSelected = sortedAndFilteredDevices.length > 0 && sortedAndFilteredDevices.every(d => selectedSerials.has(d.SerialNumber));
  const someFilteredSelected = sortedAndFilteredDevices.some(d => selectedSerials.has(d.SerialNumber));
  const selectedDevices = useMemo(() => 
    sortedAndFilteredDevices.filter(d => selectedSerials.has(d.SerialNumber)),
    [sortedAndFilteredDevices, selectedSerials]
  );

  const plannedRegistrationCount = useMemo(
    () =>
      selectedDevices.filter((d) =>
        serialToRegistration[d.SerialNumber]?.plannedDeviceName?.trim()
      ).length,
    [selectedDevices, serialToRegistration]
  );

  const handleToggleSelect = (device) => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (next.has(device.SerialNumber)) next.delete(device.SerialNumber);
      else next.add(device.SerialNumber);
      return next;
    });
  };

  const handleResetSoftwareUpdate = async (device, event) => {
    event?.stopPropagation?.();
    if (!device?.UDID) return;
    setSuBusy(device.UDID);
    try {
      await softwareUpdateService.setDeviceSoftwareUpdate(device.UDID, {
        mode: "inherit",
        serialNumber: device.SerialNumber,
      });
      toast.success("OS-Update folgt wieder der Gruppe.");
      const data = await softwareUpdateService.lookupPolicies([device.UDID]);
      setSuPolicies((prev) => ({ ...prev, ...(data.policies || {}) }));
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setSuBusy("");
    }
  };

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedSerials(prev => {
        const next = new Set(prev);
        sortedAndFilteredDevices.forEach(d => next.delete(d.SerialNumber));
        return next;
      });
    } else {
      setSelectedSerials(prev => {
        const next = new Set(prev);
        sortedAndFilteredDevices.forEach(d => next.add(d.SerialNumber));
        return next;
      });
    }
  };

  const platformForType = (type) => {
    if (type === 'mac') return 'mac';
    if (type === 'iphone' || type === 'ipad') return 'ios';
    if (type === 'appletv') return 'tvos';
    return 'mac';
  };

  const handleInventoryRefresh = async () => {
    if (selectedDevices.length === 0) return;
    const devices = selectedDevices
      .filter((d) => d.UDID)
      .map((d) => ({ udid: d.UDID, platform: platformForType(d.type) }));
    if (devices.length === 0) return;
    setActionLoading(true);
    try {
      await updateDevicesInventory(devices, { silent: true });
      toast.success(`Inventur-Update für ${devices.length} Gerät(e) ausgelöst.`);
      setSelectedSerials(new Set());
      refetch?.();
      schedulePostActionRefetch("Inventur-Update");
    } catch (e) {
      toast.error("Fehler beim Inventur-Update.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestart = async () => {
    if (selectedDevices.length === 0) return;
    setActionLoading(true);
    try {
      for (const d of selectedDevices) {
        if (d.UDID) restartDevice(d.UDID, false);
      }
      toast.success(`Neustart für ${selectedDevices.length} Gerät(e) ausgelöst.`);
      setSelectedSerials(new Set());
      schedulePostActionRefetch("Neustart");
    } catch (e) {
      toast.error("Fehler beim Neustart.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRename = () => {
    if (selectedDevices.length === 0) return;
    setRenameTemplate("");
    setRenameIgnorePlanned(false);
    setShowRenameModal(true);
  };

  /** Platzhalter {n} (1..n), {serial} */
  const expandRenameTemplate = (template, index, device) =>
    String(template)
      .replace(/{n}/g, String(index + 1))
      .replace(/{serial}/g, device.SerialNumber || '');

  const handleInstallProfile = () => {
    if (selectedDevices.length === 0) return;
    setShowInstallProfileModal(true);
  };

  const handleGroups = () => {
    if (selectedDevices.length === 0) return;
    setSelectedGroupIds([]);
    setShowGroupModal(true);
  };

  const toggleGroupId = (id) => {
    setSelectedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectedDevicesPayload = () =>
    selectedDevices
      .filter((d) => d.SerialNumber || d.UDID)
      .map((d) => ({
        serialNumber: d.SerialNumber,
        udid: d.UDID,
        deviceName: d.QueryResponses?.DeviceName,
        assetTag: d.assetTag,
        location: d.location?._id || d.location,
      }));

  const applyGroupMembership = async (mode) => {
    if (selectedGroupIds.length === 0) {
      toast.error("Bitte mindestens eine Gruppe wählen.");
      return;
    }
    const devices = selectedDevicesPayload();
    if (!devices.length) {
      toast.error("Die Auswahl enthält keine Geräte mit Seriennummer oder UDID.");
      return;
    }
    setActionLoading(true);
    try {
      let affected = 0;
      for (const groupId of selectedGroupIds) {
        const result = mode === "remove"
          ? await deviceGroupService.removeGroupMembers(groupId, { devices })
          : await deviceGroupService.addGroupMembers(groupId, { devices });
        affected += Number(result?.added || result?.removed || 0);
      }
      const verb = mode === "remove" ? "entfernt aus" : "hinzugefügt zu";
      if (mode === "remove" && affected === 0) {
        toast.info("Kein ausgewähltes Gerät war Mitglied der gewählten Gruppe(n).");
      } else {
        toast.success(
          `${selectedDevices.length} Gerät(e) ${verb} ${selectedGroupIds.length} Gruppe(n).`
        );
      }
      setShowGroupModal(false);
      setSelectedSerials(new Set());
      refetch?.();
      schedulePostActionRefetch("Gruppenzuweisung");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Gruppenzuweisung fehlgeschlagen.");
    } finally {
      setActionLoading(false);
    }
  };

  const doInstallProfile = (configProfile) => {
    if (!configProfile?.MobileConfigData) return;
    const profileObject = {
      PayloadDisplayName: configProfile.PayloadDisplayName ?? 'Unbekannt',
      PayloadDescription: configProfile.PayloadDescription ?? '',
      PayloadOrganization: configProfile.PayloadOrganization ?? '',
      PayloadIdentifier: configProfile.PayloadIdentifier ?? '',
      PayloadUUID: configProfile.PayloadUUID ?? '',
      MobileConfigData: configProfile.MobileConfigData,
    };
    setActionLoading(true);
    try {
      selectedDevices.forEach((d) => {
        if (d.UDID) installProfile(d.UDID, profileObject);
      });
      toast.success(`Profil „${configProfile.PayloadDisplayName}“ wird auf ${selectedDevices.length} Gerät(e) installiert.`);
      setShowInstallProfileModal(false);
      setSelectedSerials(new Set());
      schedulePostActionRefetch("Profil-Installation");
    } catch (e) {
      toast.error("Fehler beim Profil-Install.");
    } finally {
      setActionLoading(false);
    }
  };

  /** Nur Namen aus Registrierung (plannedDeviceName) – z. B. erneut anstoßen wenn MDM hängen blieb */
  const doRenamePlannedOnly = async () => {
    if (selectedDevices.length === 0) return;
    setActionLoading(true);
    try {
      let applied = 0;
      let skipped = 0;
      for (const d of selectedDevices) {
        const reg = serialToRegistration[d.SerialNumber];
        const plannedName = reg?.plannedDeviceName?.trim();
        if (!plannedName || !d.UDID) {
          skipped++;
          continue;
        }
        const platform = d.type === 'mac' ? 'macOS' : (d.type === 'appletv' ? 'tvOS' : 'iOS');
        renameDevice(d.UDID, platform, plannedName, d.QueryResponses?.DeviceName);
        applied++;
      }
      if (applied === 0) {
        toast.error("Keines der ausgewählten Geräte hat einen geplanten Namen in der Registrierung.");
        return;
      }
      toast.success(`Namensvorgabe aus Registrierung: Umbenennung für ${applied} Gerät(e) ausgelöst.`);
      if (skipped > 0) {
        toast.info(`${skipped} Gerät(e) ohne geplanten Namen übersprungen.`);
      }
      setShowRenameModal(false);
      setSelectedSerials(new Set());
      refetch?.();
      schedulePostActionRefetch("Umbenennung");
    } catch (e) {
      toast.error("Fehler beim Umbenennen.");
    } finally {
      setActionLoading(false);
    }
  };

  /** Vorlage mit {n}/{serial}; optional Registrierung überspringen */
  const doRenameWithTemplate = async () => {
    if (selectedDevices.length === 0) return;
    const template = renameTemplate.trim();
    const hasPlanned = selectedDevices.some((d) =>
      serialToRegistration[d.SerialNumber]?.plannedDeviceName?.trim()
    );

    if (renameIgnorePlanned) {
      if (!template) {
        toast.error("Bitte eine Namensvorlage eintragen (oder Haken „Nur Vorlage“ entfernen).");
        return;
      }
    } else {
      if (!template && !hasPlanned) {
        toast.error(
          "Bitte eine Vorlage eintragen oder „Geplante Namen aus Registrierung anwenden“ nutzen."
        );
        return;
      }
    }

    setActionLoading(true);
    try {
      let applied = 0;
      for (let i = 0; i < selectedDevices.length; i++) {
        const d = selectedDevices[i];
        const reg = serialToRegistration[d.SerialNumber];
        const plannedName = reg?.plannedDeviceName?.trim();
        let name;
        if (renameIgnorePlanned) {
          name = expandRenameTemplate(template, i, d);
        } else {
          name =
            plannedName ||
            (template ? expandRenameTemplate(template, i, d) : null);
        }
        if (!name?.trim() || !d.UDID) continue;
        const platform = d.type === 'mac' ? 'macOS' : (d.type === 'appletv' ? 'tvOS' : 'iOS');
        renameDevice(d.UDID, platform, name.trim(), d.QueryResponses?.DeviceName);
        applied++;
      }
      if (applied === 0) {
        toast.error("Kein gültiger Name für die ausgewählten Geräte ermittelbar.");
        return;
      }
      toast.success(`Umbenennung nach Vorlage für ${applied} Gerät(e) ausgelöst.`);
      setShowRenameModal(false);
      setSelectedSerials(new Set());
      refetch?.();
      schedulePostActionRefetch("Umbenennung");
    } catch (e) {
      toast.error("Fehler beim Umbenennen.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNanoCmdCmdPlan = () => {
    if (selectedDevices.length === 0) return;
    setShowNanoCmdModal(true);
  };

  const handleCheckAvailableUpdates = async () => {
    if (selectedDevices.length === 0) return;
    setActionLoading(true);
    try {
      let issued = 0;
      for (const d of selectedDevices) {
        if (!d.UDID) continue;
        await getAvailableSoftwareUpdates(d.UDID);
        issued++;
      }
      toast.success(`Update-Suche für ${issued} Gerät(e) ausgelöst.`);
      schedulePostActionRefetch("Update-Suche");
    } catch (e) {
      toast.error("Fehler bei der Update-Suche.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkInstallUpdates = async () => {
    if (selectedDevices.length === 0) return;
    setActionLoading(true);
    try {
      let issued = 0;
      let failed = 0;
      for (const d of selectedDevices) {
        if (!d.UDID) continue;
        try {
          await installAvailableOSUpdate(d.UDID);
          issued++;
        } catch {
          failed++;
        }
      }
      if (issued > 0) {
        toast.success(`OS-Update InstallASAP für ${issued} Gerät(e) ausgelöst.`);
      }
      if (failed > 0) {
        toast.info(`${failed} Gerät(e) ohne installierbares verfügbares Update übersprungen.`);
      }
      schedulePostActionRefetch("OS-Update");
    } catch (e) {
      toast.error("Fehler beim Auslösen der OS-Updates.");
    } finally {
      setActionLoading(false);
    }
  };

  const doNanoCmdCmdPlan = async (cmdPlanName) => {
    if (!cmdPlanName || selectedDevices.length === 0) return;
    setActionLoading(true);
    try {
      for (const d of selectedDevices) {
        if (d.UDID) {
          await nanoCmdService.startCmdPlanWorkflow(d.UDID, cmdPlanName);
        }
      }
      toast.success(`NanoCMD Command Plan „${cmdPlanName}“ wurde für ${selectedDevices.length} Gerät(e) gestartet.`);
      setShowNanoCmdModal(false);
      setSelectedSerials(new Set());
      schedulePostActionRefetch("NanoCMD");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Fehler beim Start des NanoCMD Command Plans.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="container">
      {/* Zeile 1: nur Filter — zentriert über der Tabelle */}
      <div
        className="all-device-filters d-flex flex-wrap justify-content-center align-items-center gap-2 gap-md-3 mb-3"
      >
        <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
          <span className="small text-muted text-nowrap">Gerätetyp:</span>
          {DEVICE_TYPES.map(({ value, label }) => (
            <label key={value} className="form-check form-check-inline mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                checked={!deviceTypeFilters?.length || deviceTypeFilters.includes(value)}
                onChange={() => {
                  const current = deviceTypeFilters || [];
                  const next = current.includes(value)
                    ? current.filter(t => t !== value)
                    : [...current, value];
                  onDeviceTypeFiltersChange?.(next.length === 4 ? [] : next);
                }}
              />
              <span className="form-check-label small">{label}</span>
            </label>
          ))}
        </div>
        {locations.length > 0 && (
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 'min(100%, 180px)' }}
            value={(locationFilters?.length ? locationFilters[0] : '') || locationFilter || ''}
            onChange={(e) => {
              const v = e.target.value;
              onLocationFiltersChange?.(v ? [v] : []);
              onLocationFilterChange?.(v);
            }}
          >
            <option value="">Alle Locations</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.schoolNumber} – {loc.name}
              </option>
            ))}
          </select>
        )}
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', minWidth: 'min(100%, 140px)' }}
          value={checkinRange}
          onChange={(e) => onCheckinRangeChange?.(e.target.value)}
        >
          {CHECKIN_RANGES.map(({ value, label }) => (
            <option key={value || 'all'} value={value}>{label}</option>
          ))}
        </select>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="OS Version"
          style={{ width: '100px', maxWidth: '100%' }}
          value={osVersionFilter}
          onChange={(e) => onOsVersionFilterChange?.(e.target.value)}
        />
        {groups.length > 0 && (
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto', minWidth: 'min(100%, 140px)' }}
            value={groupFilters?.[0] || ''}
            onChange={(e) => {
              const v = e.target.value;
              onGroupFiltersChange?.(v ? [v] : []);
            }}
          >
            <option value="">Gruppen: Alle</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>{g.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Zeile 2: Geräteaktionen links | Suche + Export rechts; schmal: untereinander, gleiche Ausrichtung */}
      <div
        className={
          'all-device-toolbar-row d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3 mb-4' +
          (someFilteredSelected && isAdmin ? ' justify-content-md-between' : ' justify-content-md-end')
        }
      >
        {someFilteredSelected && isAdmin && (
          <div className="d-flex flex-wrap gap-1 justify-content-start align-items-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleInventoryRefresh}
              disabled={actionLoading}
              title="Inventur aktualisieren"
            >
              <FaSync className={actionLoading ? 'spin' : ''} />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleRestart}
              disabled={actionLoading}
              title="Neustart"
            >
              <FaPowerOff />
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleInstallProfile}
              disabled={actionLoading}
              title="Profil installieren"
            >
              <FaDownload />
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleGroups}
              disabled={actionLoading || groups.length === 0}
              title="Gruppen hinzufügen oder entfernen"
            >
              <FaUsers />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCheckAvailableUpdates}
              disabled={actionLoading}
              title="Verfügbare OS-Updates abrufen"
            >
              <FaCloudDownloadAlt />
            </button>
            <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              onClick={handleBulkInstallUpdates}
              disabled={actionLoading}
              title="Verfügbare OS-Updates sofort installieren (bulk)"
            >
              <FaArrowCircleUp />
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleRename}
              disabled={actionLoading}
              title="Umbenennen"
            >
              <FaEdit />
            </button>
            {nanocmdAvailable && nanocmdCmdPlans.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleNanoCmdCmdPlan}
                disabled={actionLoading}
                title="NanoCMD Command Plan erneut ausführen (z.B. Profilinstallation)"
              >
                NanoCMD
              </button>
            )}
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end ms-md-auto w-100 w-md-auto">
          <div className="all-device-search" style={{ minWidth: 'min(100%, 200px)' }}>
            <SearchBar
              searchHandler={(query) => setSearchQuery(query)}
              value={searchQuery}
            />
          </div>
          <DeviceExportDropdown
            deviceTypes={['mac', 'iphone', 'ipad', 'appletv']}
            serialNumbersByType={exportSerialNumbersByType}
          />
        </div>
      </div>

      {showGroupModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Gruppen für {selectedDevices.length} Gerät(e)</h5>
                <button type="button" className="btn-close" onClick={() => setShowGroupModal(false)} />
              </div>
              <div className="modal-body">
                {groups.length === 0 ? (
                  <p className="text-muted small mb-0">Keine Geräte-Gruppen vorhanden.</p>
                ) : (
                  <div style={{ maxHeight: 280, overflow: 'auto' }}>
                    {groups.map((grp) => (
                      <label key={grp._id} className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedGroupIds.includes(grp._id)}
                          onChange={() => toggleGroupId(grp._id)}
                        />
                        <span className="form-check-label">
                          {grp.name}
                          {grp.location?.name ? (
                            <span className="small text-muted">
                              {' '}· {grp.location.schoolNumber ? `${grp.location.schoolNumber} – ` : ''}{grp.location.name}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={actionLoading || selectedGroupIds.length === 0}
                  onClick={() => applyGroupMembership('remove')}
                >
                  Aus Gruppe entfernen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={actionLoading || selectedGroupIds.length === 0}
                  onClick={() => applyGroupMembership('add')}
                >
                  Zur Gruppe hinzufügen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInstallProfileModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Profil auf {selectedDevices.length} Gerät(e) installieren</h5>
                <button type="button" className="btn-close" onClick={() => setShowInstallProfileModal(false)} />
              </div>
              <div className="modal-body">
                {configProfiles.length === 0 ? (
                  <p className="text-muted small">Keine Profile vorhanden. Bitte zuerst ein Profil hochladen.</p>
                ) : (
                  <div className="list-group">
                    {configProfiles.map((p) => (
                      <button
                        key={p._id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => doInstallProfile(p)}
                      >
                        {p.PayloadDisplayName || p.PayloadIdentifier || 'Unbekannt'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInstallProfileModal(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRenameModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Geräte umbenennen</h5>
                <button type="button" className="btn-close" onClick={() => setShowRenameModal(false)} />
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-3">
                  <strong>{selectedDevices.length}</strong> Gerät(e) ausgewählt.
                  {plannedRegistrationCount > 0 ? (
                    <> Davon <strong>{plannedRegistrationCount}</strong> mit geplantem Namen in der Registrierung.</>
                  ) : (
                    <> Kein geplanter Name in der Registrierung hinterlegt – nutzen Sie die Vorlage unten oder tragen Sie Namen in der Geräteregistrierung ein.</>
                  )}
                </p>

                <div className="border rounded p-3 mb-3 bg-light">
                  <div className="fw-semibold small mb-1">Namensvorgabe aus Registrierung</div>
                  <p className="small text-muted mb-2">
                    Wendet den bei der Registrierung gespeicherten Namen pro Gerät an.
                    <span className="d-block mt-1">
                      <strong>Erneut anstoßen:</strong> Wenn der Umbenennen-Befehl nicht ankam oder hängen blieb,
                      können Sie dieselbe Aktion hier erneut ausführen.
                    </span>
                  </p>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={doRenamePlannedOnly}
                    disabled={actionLoading || plannedRegistrationCount === 0}
                    title={plannedRegistrationCount === 0 ? 'Keine geplanten Namen vorhanden' : 'Geplante Namen anwenden'}
                  >
                    Geplante Namen anwenden
                  </button>
                </div>

                <div className="border rounded p-3">
                  <div className="fw-semibold small mb-1">Namensvorlage (frei wählbar)</div>
                  <p className="small text-muted mb-2">
                    Platzhalter: <code className="font-monospace">{'{n}'}</code> laufende Nummer (1 … n),{' '}
                    <code className="font-monospace">{'{serial}'}</code> Seriennummer.
                    Ohne Haken unten: Registrierungs-Name hat Vorrang, falls vorhanden.
                  </p>
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="renameIgnorePlanned"
                      checked={renameIgnorePlanned}
                      onChange={(e) => setRenameIgnorePlanned(e.target.checked)}
                    />
                    <label className="form-check-label small" htmlFor="renameIgnorePlanned">
                      Nur Vorlage verwenden (Registrierungs-Namen ignorieren)
                    </label>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder='z.B. iPad_{n} oder Raum-{serial}'
                    value={renameTemplate}
                    onChange={(e) => setRenameTemplate(e.target.value)}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={doRenameWithTemplate}
                    disabled={actionLoading}
                  >
                    Nach Vorlage umbenennen
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRenameModal(false)}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNanoCmdModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">NanoCMD Command Plan ausführen</h5>
                <button type="button" className="btn-close" onClick={() => setShowNanoCmdModal(false)} />
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-2">
                  Startet den Enrollment-Command-Plan erneut auf {selectedDevices.length} Gerät(en). Nützlich, wenn die automatische Profilinstallation beim ersten Check-in fehlgeschlagen ist.
                </p>
                {nanocmdCmdPlans.length === 0 ? (
                  <p className="text-muted small">
                    Keine Command Plans konfiguriert. Setze NANOCMD_CMDPLANS in der Backend-Umgebung (z.B. enrollment).
                  </p>
                ) : (
                  <div className="list-group">
                    {nanocmdCmdPlans.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => doNanoCmdCmdPlan(p.name)}
                      >
                        <strong>{p.name}</strong>
                        {p.profile_names?.length > 0 && (
                          <span className="text-muted small ms-2">({p.profile_names.length} Profil(e))</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNanoCmdModal(false)}>Abbrechen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {sortedAndFilteredDevices.length === 0 ? (
        <p className="text-center my-4">Keine Geräte gefunden</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allFilteredSelected}
                      ref={(el) => el && (el.indeterminate = someFilteredSelected && !allFilteredSelected)}
                      onChange={handleToggleSelectAll}
                      title={allFilteredSelected ? 'Alle abwählen' : 'Alle auswählen'}
                    />
                  </th>
                )}
                <th>Typ</th>
                <th onClick={() => requestSort('SerialNumber')} className="cursor-pointer select-none">Serial Number {renderSortArrow('SerialNumber')}</th>
                <th onClick={() => requestSort('DeviceName')} className="cursor-pointer select-none">Name {renderSortArrow('DeviceName')}</th>
                <th onClick={() => requestSort('Model')} className="cursor-pointer select-none">Model {renderSortArrow('Model')}</th>
                <th onClick={() => requestSort('Location')} className="cursor-pointer select-none">Location {renderSortArrow('Location')}</th>
                <th onClick={() => requestSort('AssetTag')} className="cursor-pointer select-none">Asset Tag {renderSortArrow('AssetTag')}</th>
                <th>Benutzer</th>
                <th onClick={() => requestSort('OSVersion')} className="cursor-pointer select-none">OS Version {renderSortArrow('OSVersion')}</th>
                <th>OS-Ziel</th>
                <th onClick={() => requestSort('updatedAt')} className="cursor-pointer select-none">Checked In {renderSortArrow('updatedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredDevices.map((device) => (
                <AllDeviceRow
                  key={device.SerialNumber}
                  device={device}
                  isSelected={selectedSerials.has(device.SerialNumber)}
                  onToggleSelect={isAdmin ? handleToggleSelect : undefined}
                  suPolicy={suPolicies[device.UDID]}
                  suBusy={suBusy === device.UDID}
                  onResetSoftwareUpdate={isAdmin ? handleResetSoftwareUpdate : undefined}
                  onAssignUser={staff ? setAssignTarget : undefined}
                />
              ))}
            </tbody>
          </table>

          <div className="mt-4 d-flex gap-3 justify-content-center flex-wrap">
            {['macs', 'iphones', 'ipads', 'appletvs'].map(type => {
              const singularType = type === 'appletvs' ? 'appletv' : type.replace('s', '');
              const hasMore = hasNextPage[type];
              const loading = loadingMore[type];
              const count = deviceCounts[singularType];
              const total = totalCount[type];

              return hasMore && (
                <button
                  key={type}
                  onClick={() => onLoadMore(type)}
                  disabled={loading}
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                >
                  {loading && (
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  Load More {type.charAt(0).toUpperCase() + type.slice(1)}
                  {total && (
                    <span className="text-muted ms-1">({count}/{total})</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
      <AssignUserModal
        visible={Boolean(assignTarget)}
        udid={assignTarget?.UDID}
        serialNumber={assignTarget?.SerialNumber}
        currentUser={assignTarget?.assignedDirectoryUser}
        onClose={() => setAssignTarget(null)}
        onSaved={() => {
          setAssignTarget(null);
          if (typeof refetch === "function") refetch();
        }}
      />
    </div>
  );
}
